import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CommentBody, CommentBodyInlineElement } from "@liveblocks/core";
import { useOthers, useSelf } from "@liveblocks/react";
import { useStore } from "store";
import { Avatar } from "./CommentItem";
import { useCompanyUsers } from "./useCompanyUsers";
import { getCachedUser, subscribeUser } from "./mentionCache";

type Props = {
  placeholder?: string;
  autoFocus?: boolean;
  initialText?: string;
  /**
   * When editing an existing comment, pass the original body so the composer
   * can hydrate both the plain text AND the mention tokens. Without this,
   * `@mentions` would render as empty in the textarea and silently get
   * dropped on save.
   */
  initialBody?: CommentBody;
  submitLabel?: string;
  /**
   * Receives the parsed comment body plus any image files the user attached
   * via the paperclip button or drag-and-drop. Callers are responsible for
   * uploading the files to Liveblocks (`room.uploadAttachment`) and passing
   * the resulting `CommentAttachment[]` to `createThread` / `createComment`.
   */
  onSubmit: (body: CommentBody, files: File[]) => void | Promise<void>;
  onCancel?: () => void;
  compact?: boolean;
  /** Hide the attach button + drop zone (e.g. when editing an existing comment). */
  disableAttachments?: boolean;
};

// Hard caps so the user can't queue a 200MB photo. Liveblocks itself caps
// uploads on the server side, but bouncing it locally gives a much faster
// failure mode.
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_ATTACHMENTS = 5;
const ACCEPTED_IMAGE_MIME = /^image\//;

type MentionToken = {
  /** The literal text that appears in the textarea for this mention, e.g. "@Jane Doe". */
  label: string;
  /** The Liveblocks user id this token refers to. */
  userId: string;
};

type Suggestion = {
  userId: string;
  name: string;
  email?: string;
};

/**
 * Build a CommentBody from plain text + an ordered list of mention tokens.
 * Each token is matched by its literal label ("@Name") in order of appearance;
 * if the user has edited/deleted the label, it's ignored and the text remains plain.
 */
const buildCommentBody = (text: string, mentions: MentionToken[]): CommentBody => {
  const inlines: CommentBodyInlineElement[] = [];
  let cursor = 0;
  // Walk through mentions in order
  for (const m of mentions) {
    const idx = text.indexOf(m.label, cursor);
    if (idx === -1) continue; // user removed/edited this mention's label
    if (idx > cursor) {
      inlines.push({ text: text.slice(cursor, idx) });
    }
    inlines.push({ type: "mention", id: m.userId });
    cursor = idx + m.label.length;
  }
  if (cursor < text.length) {
    inlines.push({ text: text.slice(cursor) });
  }
  if (inlines.length === 0) inlines.push({ text });
  return {
    version: 1,
    content: [{ type: "paragraph", children: inlines as any }],
  };
};

export const CommentComposer: React.FC<Props> = ({
  placeholder = "Add a comment…",
  autoFocus = false,
  initialText = "",
  initialBody,
  submitLabel = "Send",
  onSubmit,
  onCancel,
  compact,
  disableAttachments = false,
}) => {
  const self = useSelf();
  const others = useOthers();
  const storeUser = useStore((s: any) => s.user);

  // When editing, hydrate text + mention tokens from the original body so
  // mentions remain editable and survive a save without manual re-tagging.
  // We only depend on `initialBody` here — every mention starts as a unique
  // placeholder ("@user-<uid>"), and the resolution effect below fills in
  // real names when self/presence/cache become available. This keeps the
  // memo deps honest instead of silently capturing self/storeUser/others.
  const initialFromBody = useMemo(() => {
    if (!initialBody?.content?.length) return null;
    let txt = "";
    const tokens: MentionToken[] = [];
    for (const block of initialBody.content) {
      if (block.type !== "paragraph") continue;
      for (const inline of block.children) {
        if ("text" in inline) {
          txt += inline.text;
        } else if (inline.type === "mention") {
          const label = `@user-${inline.id}`;
          tokens.push({ label, userId: inline.id });
          txt += label;
        } else if (inline.type === "link") {
          txt += inline.text || inline.url;
        }
      }
    }
    return { text: txt, mentions: tokens };
  }, [initialBody]);

  const [text, setText] = useState(initialFromBody?.text ?? initialText);
  const [mentions, setMentions] = useState<MentionToken[]>(initialFromBody?.mentions ?? []);

  // Resolve any "@user-<uid>" placeholder into the real "@Name" once the
  // matching info shows up — covers self (via zustand store), other users
  // currently in presence, and the async mention cache. Re-runs when
  // self/storeUser/others change, so a late-loading storeUser still patches
  // the self-mention label.
  useEffect(() => {
    if (!initialFromBody) return;
    const replace = (uid: string, name: string) => {
      const placeholder = `@user-${uid}`;
      const newLabel = `@${name}`;
      setMentions((prev) => {
        let changed = false;
        const next = prev.map((m) => {
          if (m.userId === uid && m.label === placeholder) {
            changed = true;
            return { ...m, label: newLabel };
          }
          return m;
        });
        return changed ? next : prev;
      });
      setText((prev) => prev.split(placeholder).join(newLabel));
    };

    const unsubs: Array<() => void> = [];
    for (const m of initialFromBody.mentions) {
      const uid = m.userId;
      if (self?.id === uid) {
        const name =
          [storeUser?.firstName, storeUser?.lastName].filter(Boolean).join(" ") ||
          storeUser?.email ||
          (self.info as any)?.name;
        if (name) replace(uid, name);
        continue;
      }
      const cached = getCachedUser(uid);
      if (cached?.name) {
        replace(uid, cached.name);
        continue;
      }
      const presence = others.find((o) => o.id === uid);
      const presenceName = presence ? ((presence.info as any)?.name as string | undefined) : undefined;
      if (presenceName) replace(uid, presenceName);
      // Subscribe regardless of presence-fallback; cache is the source of truth.
      unsubs.push(
        subscribeUser(uid, () => {
          const r = getCachedUser(uid);
          if (r?.name) replace(uid, r.name);
        })
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [initialFromBody, self, storeUser, others]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);

  // Render the textarea content with mention labels wrapped in styled chips.
  // We mirror the textarea inside an overlay div (same font/padding/wrap)
  // and make the textarea's text transparent so the user only sees the
  // styled mirror — caret + selection still come from the textarea.
  const highlightedNodes = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    // Sort by current position in the text so forward-scanning works even
    // when the user rearranged content (e.g. moved "@Bob" before "@Alice").
    // Stable sort + cursor advance still handles duplicate labels correctly.
    const ordered = [...mentions]
      .map((m) => ({ m, at: text.indexOf(m.label) }))
      .filter((x) => x.at !== -1)
      .sort((a, b) => a.at - b.at)
      .map((x) => x.m);
    let cursor = 0;
    for (const m of ordered) {
      const idx = text.indexOf(m.label, cursor);
      if (idx === -1) continue;
      if (idx > cursor) nodes.push(text.slice(cursor, idx));
      nodes.push(
        <span
          key={`m-${idx}-${m.userId}`}
          // Background + color only — NO padding/margin/border, otherwise
          // character positions would drift relative to the textarea.
          className="rounded bg-[#e2e2fc]/80 text-[#4f5b94]"
        >
          {m.label}
        </span>
      );
      cursor = idx + m.label.length;
    }
    if (cursor < text.length) nodes.push(text.slice(cursor));
    // A trailing newline in a textarea collapses unless we add a sentinel
    // character; zero-width space keeps the mirror's height in sync.
    nodes.push("​");
    return nodes;
  }, [text, mentions]);

  // Keep the mirror's scroll in lockstep with the textarea (the textarea
  // hides its scrollbar via CSS but still scrolls vertically when content
  // exceeds 160px max-height).
  const handleScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
    if (mirrorRef.current) {
      mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
      mirrorRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  // Mention-trigger state
  const [triggerStart, setTriggerStart] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; left: number } | null>(null);

  const { users: companyUsers } = useCompanyUsers(query);

  // Build the pool of mentionable users: prefer the full company roster from
  // the platform API; fall back to self + room collaborators if the API call
  // returned nothing (e.g. CLIENT role without ROLE_CLIENT_ADMIN).
  const userPool: Suggestion[] = useMemo(() => {
    const pool: Suggestion[] = [];
    const seen = new Set<string>();
    const push = (u: Suggestion) => {
      if (!u.userId || seen.has(u.userId)) return;
      seen.add(u.userId);
      pool.push(u);
    };

    // Company roster (primary source)
    for (const u of companyUsers) {
      push({ userId: u.id, name: u.name, email: u.email });
    }

    // Always make sure self is selectable
    if (self?.id) {
      const selfName =
        [storeUser?.firstName, storeUser?.lastName].filter(Boolean).join(" ") ||
        storeUser?.email ||
        (self.info as any)?.name ||
        "You";
      push({ userId: self.id, name: selfName, email: storeUser?.email });
    }

    // Collaborators currently in the room (covers users the API didn't return)
    for (const o of others) {
      if (!o?.id) continue;
      const info = (o.info as any) || {};
      push({ userId: o.id, name: info.name || "User" });
    }

    return pool;
  }, [
    companyUsers,
    self?.id,
    (self?.info as any)?.name,
    others,
    storeUser?.firstName,
    storeUser?.lastName,
    storeUser?.email,
  ]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return userPool.slice(0, 8);
    return userPool
      .filter((u) => u.name.toLowerCase().includes(q) || (u.email ? u.email.toLowerCase().includes(q) : false))
      .slice(0, 8);
  }, [userPool, query]);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [autoFocus]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  // Position the mention menu below the textarea
  const recomputeAnchor = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuAnchor({ top: rect.bottom + 4, left: rect.left });
  }, []);

  useLayoutEffect(() => {
    if (triggerStart !== null) recomputeAnchor();
  }, [triggerStart, recomputeAnchor]);

  // Detect / update @ trigger after text changes.
  // The captured query allows internal whitespace so multi-word names like
  // "John Doe" keep the dropdown open while the user is still typing the
  // name; we close on newline, on a second `@`, or when the query reaches a
  // pragmatic length cap.
  const detectTrigger = useCallback((value: string, caret: number) => {
    const before = value.slice(0, caret);
    const m = before.match(/(^|\s)@([^@\n]{0,30})$/);
    if (!m) {
      setTriggerStart(null);
      setQuery("");
      return;
    }
    const at = before.length - m[2].length - 1; // position of '@'
    setTriggerStart(at);
    setQuery(m[2]);
    setActiveIdx(0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const caret = e.target.selectionStart ?? value.length;

    // If editing inside a chip broke its label (e.g. "@John Doe" → "@John De"),
    // drop the broken token AND re-open the mention dropdown with whatever
    // remains after the chip's "@" so the user can pick another user without
    // having to delete everything and re-type "@". The standard `detectTrigger`
    // regex disallows spaces inside the @-token, so chips containing spaces
    // wouldn't otherwise reopen — handle that case explicitly here.
    let nextMentions = mentions;
    let brokenAtPos: number | null = null;
    if (mentions.length) {
      // Process in current text order so a rearrangement (e.g. moved "@Bob"
      // before "@Alice") doesn't fail the lookup and falsely flag a chip
      // as broken. Stable sort + cursor advance handles duplicate labels.
      const ordered = [...mentions]
        .map((m) => ({ m, at: value.indexOf(m.label) }))
        .sort((a, b) => (a.at === -1 ? 1 : b.at === -1 ? -1 : a.at - b.at))
        .map((x) => x.m);
      const surviving: MentionToken[] = [];
      let cursor = 0;
      for (const m of ordered) {
        const idx = value.indexOf(m.label, cursor);
        if (idx !== -1) {
          surviving.push(m);
          cursor = idx + m.label.length;
        }
      }
      if (surviving.length !== mentions.length) {
        nextMentions = surviving;
        // Most recent "@" before the caret is the broken chip's start.
        const at = value.slice(0, caret).lastIndexOf("@");
        if (at !== -1) brokenAtPos = at;
      }
    }

    // Auto-tag: if the user just typed a terminator (space / newline / tab)
    // immediately after an "@FullName" that exactly matches a known user,
    // promote the typed text to a chip without requiring an explicit pick
    // from the dropdown. Case-insensitive match; we substitute the canonical
    // name so the chip label stays consistent across users.
    if (brokenAtPos === null && caret > 0) {
      const justTyped = value[caret - 1];
      if (justTyped && /\s/.test(justTyped)) {
        const upto = value.slice(0, caret - 1);
        const m = upto.match(/(^|\s)@([^@\n]{1,40})$/);
        if (m) {
          const typedName = m[2];
          const candidate = userPool.find((u) => u.name.toLowerCase() === typedName.toLowerCase());
          if (candidate) {
            const at = upto.length - typedName.length - 1;
            const label = `@${candidate.name}`;
            const newValue = value.slice(0, at) + label + value.slice(at + 1 + typedName.length);
            const newCaret = at + label.length + 1; // past label + terminator
            setText(newValue);
            const filtered = nextMentions.filter((mt) => mt.userId !== candidate.userId || mt.label !== label);
            setMentions([...filtered, { label, userId: candidate.userId }]);
            setTriggerStart(null);
            setQuery("");
            requestAnimationFrame(() => {
              if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(newCaret, newCaret);
              }
            });
            return;
          }
        }
      }
    }

    if (brokenAtPos !== null) {
      // The chip just broke (user deleted a char inside it). UX rule: don't
      // leave the user editing the partial — wipe the whole "@<broken>"
      // range down to a bare "@" so they can either keep typing fresh or
      // select from the dropdown right away. We figure out how far to wipe
      // by mapping the broken mention's old-text range into the new value.
      const broken = mentions.find((m) => !nextMentions.includes(m));
      const oldAt = broken ? text.indexOf(broken.label) : -1;
      const removed = text.length - value.length;
      if (broken && oldAt === brokenAtPos && removed > 0) {
        const newEnd = oldAt + broken.label.length - removed;
        if (newEnd > oldAt + 1 && newEnd <= value.length) {
          const cleaned = value.slice(0, oldAt + 1) + value.slice(newEnd);
          const newCaret = oldAt + 1;
          setText(cleaned);
          setMentions(nextMentions);
          setTriggerStart(oldAt);
          setQuery("");
          setActiveIdx(0);
          requestAnimationFrame(() => {
            if (textareaRef.current) {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(newCaret, newCaret);
            }
          });
          return;
        }
      }
    }

    setText(value);
    if (nextMentions !== mentions) setMentions(nextMentions);

    if (brokenAtPos !== null) {
      // Fallback (e.g. couldn't determine exact chip span): keep the partial
      // as the dropdown query so the user can still finish or re-pick.
      const partial = value.slice(brokenAtPos + 1, caret);
      if (partial.length <= 50) {
        setTriggerStart(brokenAtPos);
        setQuery(partial);
        setActiveIdx(0);
        return;
      }
    }

    detectTrigger(value, caret);
  };

  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    detectTrigger(el.value, el.selectionStart ?? el.value.length);
  };

  const closeMenu = useCallback(() => {
    setTriggerStart(null);
    setQuery("");
    setMenuAnchor(null);
  }, []);

  const pickSuggestion = useCallback(
    (s: Suggestion) => {
      if (triggerStart === null) return;
      const el = textareaRef.current;
      if (!el) return;
      const caret = el.selectionStart ?? text.length;
      const label = `@${s.name}`;
      const next = text.slice(0, triggerStart) + label + " " + text.slice(caret);
      setText(next);
      setMentions((prev) => [...prev, { label, userId: s.userId }]);
      closeMenu();
      // Restore caret after the inserted mention + space
      const newCaret = triggerStart + label.length + 1;
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newCaret, newCaret);
        }
      });
    },
    [triggerStart, text, closeMenu]
  );

  // Object URLs for the preview thumbnails. We keep them in a parallel map
  // (keyed by File identity) so we can revoke them precisely when files are
  // removed or the composer unmounts — leaking these blocks GC of the image
  // bytes for the lifetime of the page.
  useEffect(() => {
    setPreviews((prev) => {
      const next: Record<string, string> = {};
      const seen = new Set<string>();
      files.forEach((f) => {
        const key = `${f.name}:${f.size}:${f.lastModified}`;
        seen.add(key);
        next[key] = prev[key] || URL.createObjectURL(f);
      });
      // Revoke any URLs that no longer correspond to a queued file
      Object.entries(prev).forEach(([k, url]) => {
        if (!seen.has(k)) URL.revokeObjectURL(url);
      });
      return next;
    });
  }, [files]);

  useEffect(() => {
    return () => {
      // Final cleanup on unmount
      Object.values(previews).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback((incoming: FileList | File[] | null | undefined) => {
    if (!incoming) return;
    const arr = Array.from(incoming);
    const accepted: File[] = [];
    const errors: string[] = [];
    for (const f of arr) {
      if (!ACCEPTED_IMAGE_MIME.test(f.type)) {
        errors.push(`${f.name} isn't an image`);
        continue;
      }
      if (f.size > MAX_ATTACHMENT_BYTES) {
        errors.push(`${f.name} exceeds 10MB`);
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => {
      const remainingSlots = MAX_ATTACHMENTS - prev.length;
      if (remainingSlots <= 0) {
        errors.push(`Only ${MAX_ATTACHMENTS} attachments per comment`);
        return prev;
      }
      const slice = accepted.slice(0, remainingSlots);
      if (accepted.length > remainingSlots) {
        errors.push(`Only ${MAX_ATTACHMENTS} attachments per comment`);
      }
      return [...prev, ...slice];
    });
    setAttachError(errors[0] || null);
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachError(null);
  }, []);

  // Tldraw installs its own drag-and-drop listeners on the canvas in the
  // capture phase to catch image drops. React's synthetic `onDrop` runs in
  // the bubble phase, so without intercepting earlier the canvas swallows
  // the drop and tries to import our screenshot as a tldraw shape. We attach
  // native listeners directly to the composer in the capture phase and stop
  // propagation there — that way tldraw never sees the event when the user
  // drops over the composer.
  useEffect(() => {
    if (disableAttachments) return;
    const el = composerRef.current;
    if (!el) return;

    const isFileDrag = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files");

    const onDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      // Tell the browser this is a copy-drop target; without setting this
      // here some Linux/Windows browsers won't fire `drop`.
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setIsDragOver(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      // Only clear when leaving the composer entirely.
      if (el.contains(e.relatedTarget as Node)) return;
      setIsDragOver(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      addFiles(e.dataTransfer?.files);
    };

    el.addEventListener("dragenter", onDragEnter, { capture: true });
    el.addEventListener("dragover", onDragOver, { capture: true });
    el.addEventListener("dragleave", onDragLeave, { capture: true });
    el.addEventListener("drop", onDrop, { capture: true });
    return () => {
      el.removeEventListener("dragenter", onDragEnter, { capture: true });
      el.removeEventListener("dragover", onDragOver, { capture: true });
      el.removeEventListener("dragleave", onDragLeave, { capture: true });
      el.removeEventListener("drop", onDrop, { capture: true });
    };
  }, [addFiles, disableAttachments]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Always prevent the paste event from bubbling to tldraw — otherwise
      // pasting into the reply box also pastes into the canvas.
      e.stopPropagation();
      if (disableAttachments) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const pasted: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) pasted.push(f);
        }
      }
      if (pasted.length > 0) {
        e.preventDefault(); // don't paste a base64-ish blob string into the textarea
        addFiles(pasted);
      }
    },
    [addFiles, disableAttachments]
  );

  // Walk the text looking for `@FullName` patterns that exactly match a known
  // user but aren't yet tracked as `MentionToken`s — happens when the user
  // edits a chip back to its original full name (e.g. deletes the trailing
  // letter, then re-types it) without typing a terminator after, so the
  // existing keystroke-time auto-tag never fires. Used at submit time and on
  // blur so the message goes out with the correct mentions even if the user
  // never touched the dropdown.
  const finalizeMentions = useCallback(
    (curText: string, curMentions: MentionToken[]): MentionToken[] => {
      if (!curText) return curMentions;
      // Mark ranges already covered by an existing mention so we don't
      // double-add or overlap.
      const claimed: Array<[number, number]> = [];
      const orderedExisting = [...curMentions]
        .map((m) => ({ m, at: curText.indexOf(m.label) }))
        .filter((x) => x.at !== -1)
        .sort((a, b) => a.at - b.at);
      let scan = 0;
      for (const { m } of orderedExisting) {
        const idx = curText.indexOf(m.label, scan);
        if (idx === -1) continue;
        claimed.push([idx, idx + m.label.length]);
        scan = idx + m.label.length;
      }

      // Prefer longer names so "@John Doe Jr" wins over "@John Doe" when
      // both exist in the pool.
      const candidates = userPool.slice().sort((a, b) => b.name.length - a.name.length);
      const added: MentionToken[] = [];
      let i = 0;
      while (i < curText.length) {
        if (curText[i] !== "@") {
          i++;
          continue;
        }
        // Word-boundary: same rule as the @-trigger regex.
        if (i > 0 && !/\s/.test(curText[i - 1])) {
          i++;
          continue;
        }
        if (claimed.some(([s, e]) => i >= s && i < e)) {
          i++;
          continue;
        }
        let matched: { label: string; userId: string; len: number } | null = null;
        for (const u of candidates) {
          const label = `@${u.name}`;
          const slice = curText.slice(i, i + label.length);
          if (slice.toLowerCase() !== label.toLowerCase()) continue;
          // Require end-of-text or non-word char after, so "@John" doesn't
          // hijack "@JohnDoe".
          const after = curText[i + label.length];
          if (after !== undefined && !/[\s.,;:!?)\]]/.test(after)) continue;
          matched = { label, userId: u.userId, len: label.length };
          break;
        }
        if (matched) {
          added.push({ label: matched.label, userId: matched.userId });
          claimed.push([i, i + matched.len]);
          i += matched.len;
        } else {
          i++;
        }
      }
      return added.length === 0 ? curMentions : [...curMentions, ...added];
    },
    [userPool]
  );

  const submit = useCallback(async () => {
    const trimmed = text.trim();
    // Allow submitting attachments-only (no text) — matches the typical
    // "drop a screenshot" flow where words are optional.
    if (!trimmed && files.length === 0) return;
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const finalMentions = finalizeMentions(trimmed, mentions);
      await onSubmit(buildCommentBody(trimmed, finalMentions), files);
      setText("");
      setMentions([]);
      setFiles([]);
      setAttachError(null);
      closeMenu();
    } finally {
      setIsSubmitting(false);
    }
  }, [text, mentions, files, onSubmit, isSubmitting, closeMenu, finalizeMentions]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (triggerStart !== null && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickSuggestion(suggestions[activeIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape" && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  const canSubmit = (text.trim().length > 0 || files.length > 0) && !isSubmitting;

  return (
    <div ref={composerRef} className={`relative flex flex-col gap-2 ${compact ? "" : "pt-1"}`}>
      <div className="relative">
        {/* Visual mirror — renders the same text as the textarea but with
            mention labels wrapped in chip-styled spans. Sits behind the
            (transparent-text) textarea via absolute positioning; padding,
            border, font, line-height and wrap rules MUST match the textarea
            exactly so character positions line up with the caret. */}
        <div
          ref={mirrorRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-[8px] border border-transparent bg-white py-2 pl-3 pr-10 text-[13px] text-[#130f26]"
          style={{ overflowWrap: "break-word", wordBreak: "break-word" }}
        >
          {highlightedNodes}
        </div>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleChange}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onScroll={handleScroll}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            // Give time for click on a suggestion to register before closing
            setTimeout(closeMenu, 150);
            // Reconcile any "@FullName" patterns that match a known user but
            // aren't tracked yet (e.g. user edited a chip back to its full
            // name without typing a terminator). The chip will appear in the
            // mirror as soon as state updates.
            const final = finalizeMentions(text, mentions);
            if (final !== mentions) setMentions(final);
          }}
          placeholder={placeholder}
          rows={1}
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", caretColor: "#130f26" }}
          className="relative w-full resize-none rounded-[8px] border border-[#e2e2fc] bg-transparent py-2 pl-3 pr-10 text-[13px] text-transparent placeholder:text-[#130f26]/40 focus:border-[#E2E2FC] focus:outline-none focus:ring-1 focus:ring-[#E2E2FC] [&::-webkit-scrollbar]:hidden"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          aria-label={submitLabel}
          title={submitLabel}
          className={`absolute right-1.5 top-[calc(50%-3px)] flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-solid bg-white shadow-sm transition disabled:cursor-not-allowed ${
            canSubmit ? "border-[#EE4360] text-[#EE4360] hover:bg-[#fde7e7]" : "border-[#E2E2FC] text-[#E2E2FC]"
          }`}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 19V5" />
            <path d="M5 12l7-7 7 7" />
          </svg>
        </button>
      </div>

      {!disableAttachments && files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => {
            const key = `${f.name}:${f.size}:${f.lastModified}`;
            const url = previews[key];
            return (
              <div
                key={key + ":" + i}
                className="group relative h-14 w-14 overflow-hidden rounded-md border border-solid border-[#e2e2fc] bg-[#fafafd]"
                title={f.name}
              >
                {url ? (
                  <img src={url} alt={f.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] text-[#130f26]/50">
                    {f.name.slice(0, 8)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-[10px] font-bold leading-none text-white opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label={`Remove ${f.name}`}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}

      {attachError && <div className="text-[11px] text-[#EE4360]">{attachError}</div>}

      {!disableAttachments && isDragOver && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-[#4f5b94] bg-[#4f5b94]/10 text-[12px] font-medium text-[#4f5b94]">
          Drop image to attach
        </div>
      )}

      {triggerStart !== null &&
        menuAnchor &&
        suggestions.length > 0 &&
        createPortal(
          <div
            data-comment-portal
            className="fixed z-[2147483647] max-h-[220px] w-[240px] overflow-y-auto rounded-lg border border-solid border-[#E2E2FC] bg-white shadow-[0_12px_32px_rgba(16,24,40,0.12)]"
            style={{ top: menuAnchor.top, left: menuAnchor.left }}
            onMouseDown={(e) => e.preventDefault() /* prevent textarea blur */}
          >
            {suggestions.map((s, i) => (
              <button
                key={s.userId}
                type="button"
                onClick={() => pickSuggestion(s)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] transition-colors ${
                  i === activeIdx ? "bg-[#F4F4FB]" : "hover:bg-[#FAFAFD]"
                }`}
              >
                <Avatar userId={s.userId} size={22} />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[#130f26]">{s.name}</span>
                  {s.email && <span className="truncate text-[10px] text-[#130f26]/50">{s.email}</span>}
                </div>
              </button>
            ))}
          </div>,
          document.body
        )}

      {(isFocused || text.length > 0 || files.length > 0) && (
        <div className="-mt-1 flex items-center justify-between">
          {!disableAttachments ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files);
                  // Reset so picking the same file again still fires `onChange`
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                // Use mousedown + preventDefault so clicking the paperclip
                // doesn't blur the textarea (which would hide this row).
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                title="Attach image"
                aria-label="Attach image"
                className="flex h-4 w-4 items-center justify-center rounded text-[#130f26]/60 transition hover:text-[#130f26]"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
            </>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {onCancel && (
              <button
                type="button"
                // mousedown + preventDefault keeps the textarea focused so
                // this row (gated on isFocused/text/files) doesn't vanish
                // before the click lands on the button — otherwise the
                // empty-state path would lose Cancel mid-click and onCancel
                // would never fire.
                onMouseDown={(e) => e.preventDefault()}
                onClick={onCancel}
                className="rounded-md px-2 py-1 text-[12px] font-medium text-[#130f26]/70 hover:bg-[#f4f4f7]"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
